<template>
  <!-- Recordatori de la cistella a les pàgines d'estudi: qui afegeix un dossier des d'un anunci i continua estudiant
       sovint no torna a la cistella (embut set.-oct. 2026: 139 afegits des del popup/llistes/carrusel, 32 al checkout).
       Surt mentre hi hagi alguna cosa a la cistella; la X l'amaga fins que la cistella canviï. -->
  <div v-if="visible" class="crb font-rubik" role="region" aria-label="Cistella">
      <div class="crb-icon">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="mdiCart"/></svg>
        <span class="crb-count">{{ units }}</span>
      </div>
      <div class="crb-text">
        <b>{{ title }}</b>
        <span>{{ subtitle }}</span>
      </div>
      <button class="crb-go" @click="go">
        Acaba la comanda
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="mdiArrowRight"/></svg>
      </button>
      <button class="crb-close" aria-label="Amaga" @click="dismiss">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="mdiClose"/></svg>
      </button>
  </div>
</template>

<script>
import {mdiArrowRight, mdiShoppingOutline as mdiCart, mdiClose} from '../../../../assets/mdi.json'
import {loadShopList, deApostrof} from './dossierShop'
import {cartVersion} from '../../../shop/cart/cart'

const HIDDEN_ON = ['/dossiers-selectivitat', '/checkout', '/admin', '/premium', '/login', '/perfil', '/upload', '/review', '/donacions']
const DISMISS_KEY = 'cart_reminder_dismissed'

function readCart(vm) {
  const cart = vm.$cookies.get('cart')
  return cart && typeof cart === 'object' ? cart : {}
}

export default {
  name: 'CartReminderBar',
  data() {
    return {mdiArrowRight, mdiCart, mdiClose, books: [], cart: {}, dismissed: null}
  },
  computed: {
    cartVersion() {
      return cartVersion.n
    },
    // Només els dossiers que encara es venen (els altres no surten a la cistella)
    units() {
      return this.lines.reduce((s, l) => s + l.quantity, 0)
    },
    lines() {
      return Object.entries(this.cart)
        .filter(([, q]) => Number(q) > 0)
        .map(([id, q]) => ({book: this.books.find(b => String(b.book_id) === id), quantity: Number(q)}))
        .filter(l => l.book)
    },
    signature() {
      return Object.entries(this.cart).filter(([, q]) => Number(q) > 0).map(([id, q]) => `${id}x${q}`).sort().join(',')
    },
    visible() {
      return this.lines.length > 0 && this.dismissed !== this.signature &&
        !HIDDEN_ON.some(p => this.$route.path.startsWith(p))
    },
    title() {
      if (this.lines.length === 1 && this.lines[0].quantity === 1) {
        const b = this.lines[0].book
        const kind = /^pack/.test(b.kind || '') ? 'Pack' : /^notes/.test(b.kind || '') ? 'Apunts' : 'Dossier'
        return `${kind} ${deApostrof(b.new_subject_name || b.subject_name)}`
      }
      return `${this.units} dossiers a la cistella`
    },
    subtitle() {
      const total = this.lines.reduce((s, l) => s + Number(l.book.price) * l.quantity, 0)
      const price = `${total.toFixed(2).replace('.', ',')} €`
      return this.units === 1 ? `És a la cistella · ${price}` : `${price} · T'arriben a casa en 1-3 dies`
    }
  },
  watch: {
    '$root.cart'() {
      this.refresh()
    },
    // També quan es canvia un llibre per un altre sense que canviï el total (el quadern pel pack)
    cartVersion() {
      this.refresh()
    },
    '$route.path'() {
      this.refresh()
    },
    visible(v) {
      if (v) this.$funnel.trackOncePerDay('ad_impression', {placement: 'cart_reminder'})
    }
  },
  mounted() {
    try {
      this.dismissed = sessionStorage.getItem(DISMISS_KEY)
    } catch (e) {
      // sessionStorage bloquejat
    }
    this.refresh()
  },
  methods: {
    deApostrof,
    async refresh() {
      this.cart = readCart(this)
      if (!Object.keys(this.cart).length || this.books.length) return
      const list = await loadShopList().catch(() => [])
      this.books = (list || []).flatMap(b => [...(b.subjects || []), ...(b.apunts || [])])
    },
    go() {
      const first = this.lines[0] && this.lines[0].book
      this.$funnel.adClick('cart_reminder', {book_id: first ? first.book_id : null, subject_id: first ? first.subject_id : null, meta: {units: this.units}})
      this.$router.push({path: '/checkout'})
    },
    dismiss() {
      this.dismissed = this.signature
      try {
        sessionStorage.setItem(DISMISS_KEY, this.signature)
      } catch (e) {
        // sessionStorage bloquejat
      }
      this.$funnel.track('ad_dismiss', {placement: 'cart_reminder', meta: {units: this.units}})
    }
  }
}
</script>

<style scoped>
.crb {
  --crb-x: -50%;
  position: fixed;
  left: 50%;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  transform: translateX(var(--crb-x));
  z-index: 20; /* per sobre de les targetes dels problemes (10), per sota dels diàlegs */
  width: calc(100% - 24px);
  max-width: 560px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px 10px 14px;
  background: #fff;
  border: 2px solid #dfe6f5;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(28, 46, 92, 0.18);
  color: rgb(46, 56, 86);
}

.crb-icon {
  position: relative;
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #eef3ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crb-icon svg {
  width: 22px;
  height: 22px;
  fill: #256eff;
}

.crb-count {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ff6b35;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.crb-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.crb-text b {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.crb-text span {
  font-size: 12.5px;
  color: #6b7590;
  margin-top: 2px;
}

.crb-go {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  background: #256eff;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.crb-go svg {
  width: 18px;
  height: 18px;
  fill: #fff;
}

.crb-go:hover {
  background: #1d5fe0;
}

.crb-close {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.crb-close svg {
  width: 18px;
  height: 18px;
  fill: #9aa3b8;
}

.crb-close:hover {
  background: #f2f4f9;
}

/* Escriptori: a la cantonada, damunt la columna d'anuncis, per no tapar «Mostrar la solució» */
@media (min-width: 960px) {
  .crb {
    --crb-x: 0;
    left: auto;
    right: 16px;
    max-width: 440px;
  }
}

@media (max-width: 480px) {
  .crb {
    gap: 10px;
    padding: 8px 6px 8px 10px;
  }

  .crb-icon {
    display: none;
  }

  .crb-text b {
    font-size: 13px;
  }

  .crb-go {
    padding: 0 12px;
    font-size: 13.5px;
  }

  .crb-go svg {
    display: none;
  }
}

/* Animació CSS i no <transition>: amb la pestanya en segon pla la transició de Vue es quedava a mitges */
@keyframes crb-in {
  from {
    opacity: 0;
    transform: translate(var(--crb-x), 16px);
  }
  to {
    opacity: 1;
    transform: translate(var(--crb-x), 0);
  }
}

.crb {
  animation: crb-in .25s ease-out;
}
</style>
